import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Settings, Grid, Bookmark, Heart, Share2, MoreHorizontal } from 'lucide-react';
import { User, Pin, Board } from '../types';
interface ProfileViewProps {
  user: User;
  savedPins: Pin[];
  boards: Board[];
  onBack: () => void;
}
export function ProfileView({
  user,
  savedPins,
  boards,
  onBack
}: ProfileViewProps) {
  const [activeTab, setActiveTab] = useState<'created' | 'saved'>('saved');
  const stats = [{
    label: 'Pins',
    value: '127'
  }, {
    label: 'Followers',
    value: '2.4k'
  }, {
    label: 'Following',
    value: '312'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="fixed inset-0 bg-white z-50 flex flex-col overflow-hidden" data-id="element-263">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-264">
        <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-id="element-265">
          <ArrowLeft className="w-5 h-5" data-id="element-266" />
        </button>
        <h1 className="font-bold text-lg" data-id="element-267">{user.username}</h1>
        <button className="p-2 -mr-2 hover:bg-gray-100 rounded-full" data-id="element-268">
          <Settings className="w-5 h-5" data-id="element-269" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-270">
        {/* Profile Info */}
        <div className="flex flex-col items-center py-6 px-4" data-id="element-271">
          <img src={user.avatar} alt={user.name} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg" data-id="element-272" />
          <h2 className="mt-4 text-xl font-bold text-gray-900" data-id="element-273">{user.name}</h2>
          <p className="text-gray-500 text-sm" data-id="element-274">{user.username}</p>

          {/* Stats */}
          <div className="flex gap-8 mt-4" data-id="element-275">
            {stats.map(stat => <div key={stat.label} className="text-center" data-id="element-276">
                <p className="font-bold text-gray-900" data-id="element-277">{stat.value}</p>
                <p className="text-xs text-gray-500" data-id="element-278">{stat.label}</p>
              </div>)}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6" data-id="element-279">
            <button className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-full font-semibold text-sm transition-colors" data-id="element-280">
              Share
            </button>
            <button className="px-6 py-2.5 bg-black text-white hover:bg-gray-800 rounded-full font-semibold text-sm transition-colors" data-id="element-281">
              Edit Profile
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 sticky top-0 bg-white z-10" data-id="element-282">
          <button onClick={() => setActiveTab('created')} className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${activeTab === 'created' ? 'text-black' : 'text-gray-500'}`} data-id="element-283">
            Created
            {activeTab === 'created' && <motion.div layoutId="profileTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" data-id="element-284" />}
          </button>
          <button onClick={() => setActiveTab('saved')} className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${activeTab === 'saved' ? 'text-black' : 'text-gray-500'}`} data-id="element-285">
            Saved
            {activeTab === 'saved' && <motion.div layoutId="profileTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" data-id="element-286" />}
          </button>
        </div>

        {/* Content */}
        {activeTab === 'saved' ? <div className="p-4" data-id="element-287">
            {/* Boards */}
            <div className="grid grid-cols-2 gap-3" data-id="element-288">
              {boards.map(board => <button key={board.id} className="text-left group" data-id="element-289">
                  <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 relative" data-id="element-290">
                    {board.coverImage ? <img src={board.coverImage} alt="" className="w-full h-full object-cover" data-id="element-291" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-292">
                        <Grid className="w-8 h-8 text-gray-300" data-id="element-293" />
                      </div>}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" data-id="element-294" />
                  </div>
                  <p className="mt-2 font-semibold text-sm text-gray-900 truncate" data-id="element-295">
                    {board.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-296">{board.pinCount} Pins</p>
                </button>)}

              {/* Create Board Button */}
              <button className="text-left" data-id="element-297">
                <div className="aspect-square rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center hover:border-gray-400 transition-colors" data-id="element-298">
                  <div className="text-center" data-id="element-299">
                    <div className="w-10 h-10 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-2" data-id="element-300">
                      <span className="text-2xl text-gray-400" data-id="element-301">+</span>
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-302">Create</p>
                  </div>
                </div>
              </button>
            </div>
          </div> : <div className="p-4" data-id="element-303">
            <div className="grid grid-cols-2 gap-2" data-id="element-304">
              {savedPins.slice(0, 8).map(pin => <div key={pin.id} className="aspect-square rounded-xl overflow-hidden bg-gray-100" data-id="element-305">
                  <img src={pin.imageUrl} alt={pin.title} className="w-full h-full object-cover" data-id="element-306" />
                </div>)}
            </div>
            {savedPins.length === 0 && <div className="text-center py-12" data-id="element-307">
                <Bookmark className="w-12 h-12 mx-auto text-gray-300 mb-3" data-id="element-308" />
                <p className="text-gray-500" data-id="element-309">No pins created yet</p>
              </div>}
          </div>}
      </div>
    </motion.div>;
}